import React, { useState, useEffect } from 'react'
import {
  Table, Button, Space, Card, Form, Input, Select,
  message, Alert, Tag, Tabs, Switch, Popconfirm, Tooltip, Drawer, Col,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, MailOutlined,
  SendOutlined, EyeOutlined, CopyOutlined,
  CheckCircleOutlined, TagsOutlined, SearchOutlined,
} from '@ant-design/icons'
import { useMasterData, type EmailTemplate } from '@/context/MasterDataContext'
import RichTextEditor from '@/components/RichTextEditor'
import { useMasterFormRoute } from '@/hooks/useMasterFormRoute'
import MasterFormShell from '@/components/MasterFormShell'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import FilterBarCard from '@/components/FilterBarCard'
import { genId } from '@/utils/genId'
import { generateCode } from '@/utils/codeGenerator'

const BASE = '/master/email-templates'

// Categories an email template can belong to — drives the dropdown + list tag
const CATEGORIES = [
  'Follow Up',
  'Proposal',
  'Invoice',
  'Payment Reminder',
  'Welcome',
  'General',
]

const CATEGORY_COLOR: Record<string, string> = {
  'Follow Up': 'blue',
  Proposal: 'geekblue',
  Invoice: 'purple',
  'Payment Reminder': 'volcano',
  Welcome: 'green',
  General: 'default',
}

// Placeholders available to managers — filled in at send time
const PLACEHOLDERS = [
  '{{companyName}}', '{{contactPerson}}', '{{proposalNumber}}', '{{proposalDate}}',
  '{{invoiceNumber}}', '{{amount}}', '{{dueDate}}',
  '{{senderName}}', '{{senderPhone}}', '{{senderEmail}}',
]

// The body a new template starts with (manager edits freely)
const DEFAULT_BODY =
  '<p>Dear {{contactPerson}},</p>' +
  '<p>&nbsp;</p>' +
  '<p>Warm regards,<br/>{{senderName}}<br/>Shakthi Power<br/>{{senderPhone}} &nbsp;|&nbsp; {{senderEmail}}</p>'

// Replace placeholders with readable sample values for the on-screen preview
const renderPreviewBody = (html: string): string =>
  html
    .replace(/\{\{\s*companyName\s*\}\}/g, 'ABC Textiles Ltd')
    .replace(/\{\{\s*contactPerson\s*\}\}/g, 'Mr. Ramesh Kumar')
    .replace(/\{\{\s*proposalNumber\s*\}\}/g, 'SP/PROP/2026/001')
    .replace(/\{\{\s*proposalDate\s*\}\}/g, '01-07-2026')
    .replace(/\{\{\s*invoiceNumber\s*\}\}/g, 'SP/INV/2026/045')
    .replace(/\{\{\s*amount\s*\}\}/g, '₹4,85,000')
    .replace(/\{\{\s*dueDate\s*\}\}/g, '10-07-2026')
    .replace(/\{\{\s*senderName\s*\}\}/g, 'Sathya Priya')
    .replace(/\{\{\s*senderPhone\s*\}\}/g, '+91 98765 43210')
    .replace(/\{\{\s*senderEmail\s*\}\}/g, 'sales@shakthipower.com')
    .replace(/\{\{[^}]+\}\}/g, '—')

const EmailTemplateMaster: React.FC = () => {
  const { emailTemplates: templates, setEmailTemplates: setTemplates, deleteEmailTemplate, saveEmailTemplate, masterDataLoaded } = useMasterData()
  const { mode, editId, isForm, goAdd, goEdit, goList } = useMasterFormRoute(BASE)
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null)
  const [bodyEditorKey, setBodyEditorKey] = useState(0) // bump to remount editor with fresh data
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('')

  const activeCount = templates.filter(t => t.status === 'active').length

  const filteredTemplates = templates.filter(t => {
    const q = searchText.toLowerCase()
    const matchSearch = !q ||
      t.name.toLowerCase().includes(q) ||
      (t.subject || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q)
    const matchStatus = !filterStatus || t.status === filterStatus
    return matchSearch && matchStatus
  })

  // Populate the form when entering the full-page add/edit route
  useEffect(() => {
    if (mode === 'new') {
      form.resetFields()
      form.setFieldsValue({
        status: 'active',
        category: 'Follow Up',
        isDefault: false,
        bodyHtml: DEFAULT_BODY,
      })
      setBodyEditorKey(k => k + 1)
    } else if (mode === 'edit') {
      const rec = templates.find(t => t.id === editId)
      if (rec) { form.setFieldsValue({ ...rec }); setBodyEditorKey(k => k + 1) }
      else goList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editId, templates])

  const onFinish = async (values: any) => {
    const isEdit = mode === 'edit'
    let id = isEdit ? editId! : ''
    if (!isEdit) {
      try { id = await generateCode('email_templates') } catch { id = genId('ET') }
    }
    const payload = { ...values, id }

    setSubmitting(true)
    try {
      await saveEmailTemplate(payload, isEdit)
      // Only one default per category — unset the others in the same
      // category, a secondary best-effort local update persisted via the
      // generic write-through sync, not this record.
      if (values.isDefault) {
        setTemplates(prev => prev.map(t =>
          t.id !== payload.id && t.category === values.category ? { ...t, isDefault: false } : t
        ))
      }
      message.success(isEdit ? 'Template updated' : 'Template created')
      goList()
    } catch { /* error already shown; stay on the form */ }
    finally { setSubmitting(false) }
  }

  const handleDuplicate = async (t: EmailTemplate) => {
    const clone = { ...t, id: genId('ET'), name: `${t.name} (Copy)`, isDefault: false }
    try {
      await saveEmailTemplate(clone, false)
      message.success('Template duplicated')
    } catch { /* error already shown */ }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteEmailTemplate(id)
      message.success('Template deleted')
      if (isForm) goList()
    } catch { /* error already shown */ }
  }

  const columns = [
    {
      title: 'Template Name',
      dataIndex: 'name',
      render: (v: string, r: EmailTemplate) => (
        <div>
          <span style={{ fontWeight: 700, fontSize: 13, color: '#1677ff' }}>
            {v}
          </span>
          {r.isDefault && (
            <Tag color="gold" style={{ marginLeft: 8, fontSize: 10 }}>Default</Tag>
          )}
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{r.description}</div>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      width: 150,
      render: (c: string) => <Tag color={CATEGORY_COLOR[c] || 'default'}>{c}</Tag>,
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      render: (s: string) => (
        <span style={{ fontSize: 12, color: '#475569' }}>{s}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (s: string) => (
        <Tag color={s === 'active' ? 'green' : 'default'}>
          {s === 'active' ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: EmailTemplate) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Tooltip title="Preview">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setPreviewTemplate(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goEdit(record.id)} />
          </Tooltip>
          <Popconfirm
            title="Delete this template?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="link" danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // ── Full-page Add / Edit form ─────────────────────────────────
  if (isForm) {
    return (
      <MasterFormShell
        title={mode === 'edit' ? '✏️ Edit Email Template' : '✨ New Email Template'}
        subtitle="Reusable email template — subject, body and placeholders"
        onBack={goList}
        extra={mode === 'edit' && editId ? (
          <Popconfirm
            title="Delete this template?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(editId)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        ) : undefined}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Tabs
            items={[
              // ── Tab 1: Basic Info ────────────────────────────────
              {
                key: 'basic',
                label: '📋 Basic Info',
                children: (
                  <div style={{ paddingTop: 12 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                      <Form.Item name="name" label="Template Name" rules={[{ required: true }]}>
                        <Input placeholder="e.g. Proposal Follow-up" />
                      </Form.Item>
                      <Form.Item name="category" label="Category" initialValue="Follow Up" rules={[{ required: true }]}>
                        <Select options={CATEGORIES.map(c => ({ value: c, label: c }))} />
                      </Form.Item>
                    </div>
                    <Form.Item name="description" label="Short Description (shown in list)">
                      <Input placeholder="e.g. Gentle follow-up after sharing a proposal" />
                    </Form.Item>
                    <Form.Item name="subject" label="Email Subject" rules={[{ required: true }]}>
                      <Input placeholder="e.g. Following up on our proposal – {{companyName}}" />
                    </Form.Item>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                      <Form.Item name="status" label="Status" initialValue="active">
                        <Select options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
                      </Form.Item>
                      <Form.Item name="isDefault" label="Default for this category" valuePropName="checked">
                        <Switch checkedChildren="Yes" unCheckedChildren="No" />
                      </Form.Item>
                    </div>
                  </div>
                ),
              },
              // ── Tab 2: Email Body (Rich Text) ────────────────────
              {
                key: 'body',
                label: '📝 Email Body',
                children: (
                  <div style={{ paddingTop: 12 }}>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Type the email body with formatting — this is what the recipient sees."
                      description={
                        <span style={{ fontSize: 12 }}>
                          Available placeholders:{' '}
                          {PLACEHOLDERS.map(p => <code key={p} style={{ marginRight: 6 }}>{p}</code>)}
                        </span>
                      }
                    />
                    <Form.Item name="bodyHtml" valuePropName="value" trigger="onChange" style={{ marginBottom: 0 }}>
                      <RichTextEditor
                        key={bodyEditorKey}
                        placeholder="Type the email body here…"
                        minHeight={320}
                      />
                    </Form.Item>
                  </div>
                ),
              },
            ]}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={goList} disabled={submitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={submitting}>
              {mode === 'edit' ? 'Save Changes' : 'Create Template'}
            </Button>
          </div>
        </Form>
      </MasterFormShell>
    )
  }

  // ── List ───────────────────────────────────────────────────────
  if (!masterDataLoaded) return <TableSkeleton columns={4} />

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MailOutlined style={{ color: '#001A4D' }} /> Email Template Master
          </h2>
          <p className="page-subtitle">
            Create and manage reusable email templates — follow-ups, proposal shares, payment
            reminders and more. Each template has a subject and body with placeholders that are
            filled in when the email is sent.
          </p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={goAdd}>
          New Template
        </Button>
      </div>

      {/* KPI */}
      <KpiCardRow
        cards={[
          { label: 'Total Templates', value: templates.length, icon: <MailOutlined style={{ fontSize: 24, color: '#001A4D' }} />, iconBg: 'rgba(0,26,77,0.08)', borderColor: '#001A4D' },
          { label: 'Active', value: activeCount, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
          { label: 'Categories', value: new Set(templates.map(t => t.category)).size, icon: <TagsOutlined style={{ fontSize: 24, color: '#F59E0B' }} />, iconBg: 'rgba(245,158,11,0.08)', borderColor: '#F59E0B' },
        ]}
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16, borderRadius: 8 }}
        message="Use placeholders like {{companyName}}, {{contactPerson}} and {{senderName}} in the subject and body — they are replaced with real values when the email is sent."
      />

      <FilterBarCard>
        <Col xs={24} sm={12} lg={10}>
          <Input.Search
            placeholder="Search by name, subject or description..."
            allowClear
            prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(v) => setSearchText(v)}
          />
        </Col>
        <Col xs={24} sm={12} lg={7}>
          <Select
            placeholder="Filter by Status"
            allowClear
            value={filterStatus || undefined}
            onChange={(v) => setFilterStatus(v || '')}
            style={{ width: '100%' }}
            options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]}
          />
        </Col>
      </FilterBarCard>

      <Card>
        <Table
          dataSource={filteredTemplates}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t: number) => `${t} templates` }}
        />
      </Card>

      {/* ── Preview Drawer ───────────────────────────────────────── */}
      <Drawer
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>✉️ Preview — {previewTemplate?.name}</div>
            <div style={{ fontSize: 12, color: '#8A93A6' }}>{previewTemplate?.description}</div>
          </div>
        }
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        width={640}
        extra={
          <Space>
            <Tooltip title="Edit">
              <Button type="text" icon={<EditOutlined />} onClick={() => { if (previewTemplate) goEdit(previewTemplate.id); setPreviewTemplate(null) }} />
            </Tooltip>
            <Tooltip title="Duplicate">
              <Button type="text" icon={<CopyOutlined />} onClick={() => { handleDuplicate(previewTemplate!); setPreviewTemplate(null) }} />
            </Tooltip>
            <Tooltip title="Delete">
              <Popconfirm
                title="Delete this template?"
                description="This action cannot be undone."
                onConfirm={() => { handleDelete(previewTemplate!.id); setPreviewTemplate(null) }}
                okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          </Space>
        }
      >
        {previewTemplate && (
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
              <div style={{ marginBottom: 6 }}>
                <Tag color={CATEGORY_COLOR[previewTemplate.category] || 'default'}>{previewTemplate.category}</Tag>
                {previewTemplate.isDefault && <Tag color="gold">Default</Tag>}
              </div>
              <div><strong>Subject:</strong> {renderPreviewBody(previewTemplate.subject)}</div>
            </div>
            <div
              style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '16px 20px' }}
              dangerouslySetInnerHTML={{ __html: renderPreviewBody(previewTemplate.bodyHtml) }}
            />
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 12 }}>
              Placeholders shown with sample values. Real values are used when the email is sent.
            </div>
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default EmailTemplateMaster
